// @ts-check
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import { Link } from '@material-ui/core';
import { validationStatusToMessageMap } from 'common/utils/provider.utils';
import { ErrorBox } from 'components/InfoBox';
import useToast from 'components/hooks/useToast';
import { useEffect, useMemo, useState } from 'react';
import useGitProviderSetupData from 'views/RegisterV2/views/ProviderSetupV2/useGitProviderSetupData';
import InviteAdmin from '../../Invite/InviteAdmin';
import { Connect } from './Connect';
import { GitProviderSelectError } from './GitProviderSelectError';
import { StyledWrapper } from './GitProvidierSelect.style';
import { StyledOnboardingErrorWrapper } from './OnboardingError.style';
import { FamiliarOrg } from './FamiliarOrg';
import { GitProviderSelectContext } from './GitProviderSelect.context';

/**
 * Git provider selection form
 * @param {object} props
 * @param {string} props.selectedProvider
 * @param {(provider:string) => void} props.setSelectedProvider
 * @param {string} props.selectedMethod
 * @param {(provider:string) => void} props.setSelectedMethod
 * @param {boolean} props.isPat
 * @param {string} [props.error]
 * @returns {JSX.Element}
 */
const GitProviderSelect = ({
  selectedProvider,
  setSelectedProvider,
  selectedMethod,
  setSelectedMethod,
  isPat,
  error: outerError
}) => {
  const setupData = useGitProviderSetupData({
    selectedProvider,
    selectedMethod,
    setSelectedMethod
  });

  const {
    failurePayload,
    isInviteAdminModalOpen,
    setIsInviteAdminModalOpen,
    familiarOrgName,
    setFailurePayload,
    confirmFamiliarOrgCreation,
    setConfirmFamiliarOrgCreation
  } = setupData;

  const isFamiliarOrg = Boolean(familiarOrgName);
  const toast = useToast();
  const [error, setError] = useState(outerError);

  useEffect(() => {
    // Authentication provider or method changed - clear error message
    setError(null);
  }, [selectedProvider, selectedMethod]);

  useEffect(() => {
    // Error message changed - show the new error message
    setError(outerError);
  }, [outerError]);

  const contextValue = useMemo(
    () => ({
      ...setupData,
      selectedProvider,
      setSelectedProvider,
      selectedMethod,
      setSelectedMethod,
      isPat,
      setConfirmFamiliarOrgCreation
    }),
    [
      isPat,
      selectedMethod,
      selectedProvider,
      setConfirmFamiliarOrgCreation,
      setSelectedMethod,
      setSelectedProvider,
      setupData
    ]
  );

  return (
    <GitProviderSelectContext.Provider value={contextValue}>
      <StyledWrapper direction='column' items='center' width='40rem'>
        <Flex height='100%' direction='column' items='center'>
          {isFamiliarOrg && !confirmFamiliarOrgCreation ? (
            <FamiliarOrg />
          ) : (
            <Connect />
          )}

          {!!failurePayload && (
            <>
              <StyledOnboardingErrorWrapper>
                <ErrorBox>
                  {
                    validationStatusToMessageMap({
                      isPat,
                      onInviteAdmin: () => setIsInviteAdminModalOpen(true),
                      missingScopes: failurePayload.missingScopes
                    })[failurePayload.tokenStatus]
                  }
                </ErrorBox>
              </StyledOnboardingErrorWrapper>
            </>
          )}

          {error && (
            <GitProviderSelectError
              error={error}
              onInviteAdminClick={() => setIsInviteAdminModalOpen(true)}
            />
          )}

          <InviteAdmin
            open={isInviteAdminModalOpen}
            setOpen={setIsInviteAdminModalOpen}
            onInviteSuccess={() => {
              toast({
                message: <>Invitation sent successfully</>,
                variant: 'success'
              });
              setFailurePayload(null);
            }}
          />

          {!error && !failurePayload && (
            <Flex width='100%' height='8rem' items='center'>
              <Text size='medium' letterSpacing='0.02rem'>
                Worried about security? Check out our{' '}
                <Link
                  data-testid='open-trust-portal-link'
                  onClick={() =>
                    window.open('https://trust.linearb.io/', '_blank')
                  }
                >
                  trust portal
                </Link>
              </Text>
            </Flex>
          )}
        </Flex>
      </StyledWrapper>
    </GitProviderSelectContext.Provider>
  );
};

export default GitProviderSelect;
